import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';

const MAX_LIST_HEIGHT = 280;

function rankOption(text, tokens) {
  let first = -1;
  for (let i = 0; i < tokens.length; i++) {
    const idx = text.indexOf(tokens[i]);
    if (idx === -1) return -1;
    if (i === 0) first = idx;
  }
  if (first === 0) return 0;
  if (/[^a-z0-9]/.test(text[first - 1])) return 1;
  return 2;
}

function filterOptions(options, query) {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return options.slice();
  const ranked = [];
  options.forEach((opt, order) => {
    const r = rankOption(String(opt).toLowerCase(), tokens);
    if (r !== -1) ranked.push({ opt, r, order });
  });
  ranked.sort((a, b) => a.r - b.r || a.order - b.order);
  return ranked.map(x => x.opt);
}

export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  className = 'form-field-input',
  disabled = false
}) {
  const [query, setQuery] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const rows = useMemo(() => {
    const text = query == null ? '' : query;
    const matches = filterOptions(options, text);
    return text.trim() ? matches : [null, ...matches];
  }, [options, query]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    function place() {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      const flip = below < MAX_LIST_HEIGHT && above > below;
      setPos({
        left: rect.left,
        width: rect.width,
        top: flip ? undefined : rect.bottom + 4,
        bottom: flip ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight: Math.min(MAX_LIST_HEIGHT, (flip ? above : below) - 12)
      });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const node = listRef.current.children[active];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = () => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  };

  const commit = (row) => {
    const next = row == null ? '' : row;
    if (next !== value && onChange) onChange(next);
    close();
  };

  const handleFocus = (e) => {
    if (disabled) return;
    setOpen(true);
    setQuery(null);
    const idx = value ? [null, ...options].findIndex(o => o === value) : -1;
    setActive(idx);
    e.target.select();
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    setActive(text.trim() ? 0 : -1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (rows.length) setActive(i => (i < rows.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (rows.length) setActive(i => (i > 0 ? i - 1 : rows.length - 1));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (active >= 0 && active < rows.length) commit(rows[active]);
      else close();
    } else if (e.key === 'Tab') {
      if (open && query && query.trim() && active >= 0 && active < rows.length && rows[active] != null) {
        commit(rows[active]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        close();
      }
    }
  };

  const shown = query == null ? (value || '') : query;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={shown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        autoComplete="off"
        onFocus={handleFocus}
        onClick={() => { if (!disabled) setOpen(true); }}
        onBlur={close}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {open && pos && ReactDOM.createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            bottom: pos.bottom,
            width: pos.width,
            maxHeight: pos.maxHeight,
            overflow: 'auto',
            zIndex: 99999,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}
        >
          {rows.map((row, i) => (
            <div
              key={row == null ? '__clear' : `${i}-${row}`}
              onMouseDown={(e) => { e.preventDefault(); commit(row); }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                background: i === active ? '#eff6ff' : 'transparent',
                color: row == null ? '#94a3b8' : '#0f172a',
                fontWeight: row != null && row === value ? 600 : 400
              }}
            >
              {row == null ? placeholder : row}
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#94a3b8' }}>No matches</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
